import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calculator, DollarSign, Users, CheckCircle2, AlertCircle, XCircle,
  ArrowRight, Info,
} from "lucide-react";
import { STATES, fplAnnual } from "@/data/legalData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const fmt = (n) => "$" + Math.round(n).toLocaleString();

export default function Eligibility() {
  const navigate = useNavigate();
  const [state, setState] = useState("");
  const [household, setHousehold] = useState("1");
  const [income, setIncome] = useState("");
  const [assets, setAssets] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const result = useMemo(() => {
    if (!submitted) return null;
    const annual = fplAnnual(state, household);
    const monthly = annual / 12;
    const t125 = monthly * 1.25;
    const t200 = monthly * 2.0;
    const inc = Number(income) || 0;
    let level;
    if (inc <= t125) level = "likely";
    else if (inc <= t200) level = "maybe";
    else level = "unlikely";
    return { annual, monthly, t125, t200, inc, level };
  }, [submitted, state, household, income]);

  const bands = {
    likely: {
      icon: CheckCircle2, color: "text-[#047857]", bg: "bg-[#ECFDF5]", border: "border-[#047857]",
      title: "You very likely qualify for a free public defender",
      body: "Your income is at or below the common eligibility threshold. Request a court-appointed attorney at your first hearing, or ask the court clerk how to apply for a public defender right away.",
    },
    maybe: {
      icon: AlertCircle, color: "text-[#B45309]", bg: "bg-[#FFFBEB]", border: "border-[#B45309]",
      title: "You may qualify — it's worth applying",
      body: "Your income is in a range where many courts still appoint counsel, sometimes with a partial fee. Eligibility rules vary by state and county, and courts also weigh dependents and expenses. Apply and explain your full financial picture.",
    },
    unlikely: {
      icon: XCircle, color: "text-[#B91C1C]", bg: "bg-[#FEF2F2]", border: "border-[#B91C1C]",
      title: "You may not automatically qualify — but you can still apply",
      body: "Your income is above the typical threshold. You can still request appointed counsel; judges consider assets, debts, dependents, and the cost/complexity of the case. If not eligible, consider a private attorney, a legal aid clinic, or a payment plan.",
    },
  };

  return (
    <div className="grain-bg min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-center">
          <span className="grid place-items-center h-14 w-14 rounded-2xl bg-forest-100 text-forest-800 mx-auto"><Calculator className="h-7 w-7" /></span>
          <span className="mt-4 block font-mono text-xs uppercase tracking-[0.18em] text-terracotta">Eligibility Calculator</span>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-semibold text-forest-900">Might you qualify for a free lawyer?</h1>
          <p className="mt-3 text-ink-700">A quick estimate based on the 2025 Federal Poverty Guidelines. This is an estimate only — the court makes the final decision.</p>
        </div>

        <div className="mt-10 rounded-3xl border border-parchment-200 bg-white p-8">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-ink-700 mb-1.5 block">Your state</Label>
              <Select value={state} onValueChange={setState}>
                <SelectTrigger data-testid="elig-state-select" className="rounded-xl border-parchment-200 h-11"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {STATES.filter((s) => s.abbr !== "US").map((s) => <SelectItem key={s.abbr} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-ink-700 mb-1.5 block flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Household size</Label>
              <Input data-testid="elig-household-input" type="number" min="1" value={household} onChange={(e) => setHousehold(e.target.value)}
                className="rounded-xl border-parchment-200 h-11" placeholder="Number of people you support" />
            </div>
          </div>

          <div className="mt-5">
            <Label className="text-ink-700 mb-1.5 block flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" /> Total monthly household income (before tax)</Label>
            <Input data-testid="elig-income-input" type="number" min="0" value={income} onChange={(e) => setIncome(e.target.value)}
              className="rounded-xl border-parchment-200 h-11" placeholder="e.g. 2400" />
          </div>

          <label className="mt-5 flex items-start gap-2.5 cursor-pointer">
            <Checkbox checked={assets} onCheckedChange={setAssets} data-testid="elig-assets-check" className="mt-0.5" />
            <span className="text-sm text-ink-700">I own significant assets (savings, property beyond a home/car). Courts may weigh these.</span>
          </label>

          <Button onClick={() => setSubmitted(true)} data-testid="elig-calculate-btn" disabled={!state || !income}
            className="mt-6 w-full rounded-xl h-12 bg-forest-800 hover:bg-forest-900 text-parchment-50 gap-2">
            Check my estimate <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        {result && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            data-testid="eligibility-result" className="mt-6 space-y-5">
            {(() => {
              const b = bands[result.level];
              const Icon = b.icon;
              return (
                <div className={`rounded-3xl border-l-4 ${b.border} ${b.bg} p-7`}>
                  <div className={`flex items-center gap-2 ${b.color}`}>
                    <Icon className="h-6 w-6" />
                    <h2 className="font-serif text-2xl font-semibold">{b.title}</h2>
                  </div>
                  <p className="mt-3 text-ink-700 leading-relaxed">{b.body}</p>
                  {assets && result.level !== "likely" && (
                    <p className="mt-3 text-sm text-ink-500">Because you noted significant assets, the court may factor those in when deciding.</p>
                  )}
                </div>
              );
            })()}

            <div className="rounded-3xl border border-parchment-200 bg-white p-7">
              <h3 className="font-semibold text-forest-900 mb-4">How your numbers compare</h3>
              <div className="space-y-3">
                <Compare label="Your monthly income" value={fmt(result.inc)} />
                <Compare label="Poverty line (100%)" value={`${fmt(result.monthly)} / mo`} />
                <Compare label="Likely-qualify line (125%)" value={`${fmt(result.t125)} / mo`} highlight={result.level === "likely"} />
                <Compare label="May-qualify line (200%)" value={`${fmt(result.t200)} / mo`} highlight={result.level === "maybe"} />
              </div>
              <div className="mt-5 flex items-start gap-2 text-xs text-ink-400">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>Thresholds for appointed counsel vary widely by state and county (some use 100%, others 200%+ of the poverty line). This tool is an estimate, not a determination.</span>
              </div>
            </div>

            <div className="rounded-3xl bg-forest-900 text-parchment-50 p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <p className="text-parchment-200/90">Ready to plan your next steps?</p>
              <div className="flex gap-3">
                <Button onClick={() => navigate("/resources", { state: { tab: "lawyer" } })} variant="outline" className="rounded-full border-parchment-50/30 bg-transparent text-parchment-50 hover:bg-parchment-50/10">Finding a lawyer</Button>
                <Button onClick={() => navigate("/diagnostic")} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-2">Run the case diagnostic <ArrowRight className="h-4 w-4" /></Button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

const Compare = ({ label, value, highlight }) => (
  <div className={`flex items-center justify-between rounded-xl px-4 py-3 ${highlight ? "bg-forest-100" : "bg-parchment-50"}`}>
    <span className="text-sm text-ink-700">{label}</span>
    <span className="font-mono text-sm font-medium text-forest-800">{value}</span>
  </div>
);
